export { DAY_MS } from 'utils/time-converter';

// The rules, one setting each (agreed in the What's New R&D, 2026-10-20).
export const NEW_USER_GRACE_DAYS = 7;
export const POPUP_GAP_DAYS = 3;
export const POPUP_WINDOW_DAYS = 30;
export const CHIP_DAYS = 30;
export const HINT_DAYS = 30;
// The panel keeps six months of posts (said so at its foot); 30.5 days a month.
export const PANEL_MONTHS = 6;
export const PANEL_DAYS = Math.ceil(PANEL_MONTHS * 30.5);
export const MAX_POPUP_PAGES = 3;
// Each user-manager call is tried this many times, this far apart. A receipt still unsaved after that
// only means its post may show once more next visit.
export const RPC_ATTEMPTS = 2;
export const RETRY_DELAY_MS = 3000;
// Ids per MarkAnnouncements call; matches user-manager's announcementMaxPostIDs.
export const MAX_MARK_POST_IDS = 50;

// The popup: picture beside the words from md up, picture above them below md, a bottom sheet on phones.
export const SPOTLIGHT_WIDE = { width: 880, height: 520 } as const;
export const SPOTLIGHT_STACKED_WIDTH_PX = 600;
export const STAGE_ASPECT = { stacked: '2 / 1', phone: '6 / 5' } as const;
// Label pills are tinted with their colour at this strength.
export const LABEL_TINT_ALPHA = 0.12;
// The progress bar's empty segments, at this strength of the text colour.
export const SEGMENT_TRACK_ALPHA = 0.14;
export const SPOTLIGHT_TRANSITION_MS = 260;
// The date on a post, e.g. "14 Oct" (date-fns pattern).
export const POST_DATE_FORMAT = 'd MMM';

// How a popup was closed, for the dismissed event. "done" = Done on the last page without the button.
export const CLOSE_METHODS = {
  close: 'close',
  esc: 'esc',
  backdrop: 'backdrop',
  done: 'done'
} as const;

// What a press on the popup led to, for the clicked event.
export const ACTION_TARGETS = {
  cta: 'cta',
  guide: 'guide'
} as const;

// Page hints point at elements carrying this attribute. Every anchor is listed here, so a post and the
// element it points at share one constant.
export const HINT_ATTRIBUTE = 'data-whats-new';
export const HINT_TARGETS = {
  certificateButton: 'certificate-button'
} as const;
// A target can render after the page settles (data loading), so it is looked for this often, this many times.
export const HINT_FIND_INTERVAL_MS = 500;
export const HINT_FIND_ATTEMPTS = 10;
export const HINT_WIDTH_PX = 300;
// Gap between the hint and its element, and the margin it keeps from the screen edges.
export const HINT_OFFSET_PX = 10;
export const HINT_EDGE_PX = 16;

// Where the panel was opened from, for the whats_new:opened event.
export const PANEL_SOURCES = {
  header: 'header',
  supportMenu: 'support_menu',
  popupLink: 'popup_link',
  link: 'link'
} as const;

// ?whats-new=<post id> (from a launch email) opens that post; an unknown or hidden one opens the panel.
export const DEEP_LINK_PARAM = 'whats-new';
// Holds that link through sign-in, which drops the address the person arrived on (sessionStorage).
export const DEEP_LINK_STORAGE_KEY = 'tc-whats-new-link';

// Dev and staging only: ?whats-new-preview=<post id>, or "demo" for the sample posts, opens the popup
// ignoring every rule and saving nothing.
export const PREVIEW_PARAM = 'whats-new-preview';
// react-admin's form theme, as JSON, so a post shows in its colours before it is saved.
export const PREVIEW_THEME_PARAM = 'whats-new-theme';
export const DEMO_PREVIEW_ID = 'demo';
// A preview ignores launch times; samples, and posts user-manager hasn't released yet, carry this one.
export const PREVIEW_LAUNCH_AT = '2026-10-14T10:00:00Z';
// Logged on dev and staging when user-manager releases a post id this build doesn't have.
export const UNKNOWN_RELEASES_WARNING = "[whats-new] user-manager released posts this build doesn't have:";

export const LEVELS = {
  launch: 'launch',
  feature: 'feature',
  fix: 'fix'
} as const;

export const LABELS = {
  new: 'new',
  improved: 'improved',
  beta: 'beta',
  fixed: 'fixed'
} as const;

// How far loading this person's state has got. "failed" still allows the panel; nothing else shows.
export const LOAD_STATUS = {
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  failed: 'failed'
} as const;

// Where a post was seen; api.ts maps these to user-manager's AnnouncementSurface enum.
export const SURFACES = {
  popup: 'popup',
  panel: 'panel',
  chip: 'chip',
  hint: 'hint'
} as const;

// The panel's filters and the labels each one shows; null = everything.
export const PANEL_FILTERS = {
  all: null,
  new: [LABELS.new, LABELS.beta],
  improved: [LABELS.improved],
  fixed: [LABELS.fixed]
} as const;
export const PANEL_WIDTHS = { xs: '100%', sm: '440px' };
// A launch's picture at the top of its card.
export const PANEL_PICTURE_HEIGHT_PX = 176;
// An unread post's card: its border and fill, at these strengths of the primary colour.
export const UNREAD_BORDER_ALPHA = 0.35;
export const UNREAD_FILL_ALPHA = 0.04;
// The filter's track, at this strength of the text colour.
export const FILTER_TRACK_ALPHA = 0.06;
// The panel's text buttons on hover, at this strength of the primary colour.
export const LINK_HOVER_ALPHA = 0.1;
// The month headings in the panel, e.g. "October 2026" (date-fns pattern).
export const PANEL_MONTH_FORMAT = 'MMMM yyyy';

// Pages where nothing may pop up: payment, maintenance and broker sign-in returns. Matched per path segment.
export const BLOCKED_ROUTES = [
  '/account-settings/billing',
  '/maintenance',
  '/accounts/reconnect',
  '/accounts/ct-reauth',
  '/accounts/tradovate'
];
// The cTrader sign-in comes back to /accounts?code=…
export const OAUTH_RETURN = { path: '/accounts', param: 'code' } as const;

// Pull-request limits checked by posts.test.ts. The id cap matches user-manager's.
export const POST_LIMITS = {
  idMaxLength: 64,
  areaMaxLength: 24,
  titleMaxLength: 60,
  ctaLabelMaxLength: 28,
  summaryMaxLength: 160,
  maxPoints: 3,
  featureMaxLength: 32,
  minSteps: 2,
  maxSteps: 4,
  stepLabelMaxLength: 16,
  stepBodyMaxLength: 200,
  maxChips: 4,
  chipMaxLength: 24,
  // The preview samples' own themes; user-manager checks every released one.
  minContrast: 4.5,
  imageMaxBytes: 300 * 1024,
  clipMaxBytes: 2 * 1024 * 1024
} as const;
// "2026-10-certificates": launch year and month, then a slug.
export const POST_ID_PATTERN = /^\d{4}-\d{2}-[a-z0-9]+(-[a-z0-9]+)*$/;
// A post's guide opens in a new tab, so only an https page.
export const GUIDE_URL_PATTERN = /^https:\/\/[^\s]+$/;
